import React, { useContext, useMemo, useEffect, useRef } from "react";
import { FinanceContextObject } from "./financeContextObject";
import { isSameMonth, parseISO } from "date-fns";
import { useAuth } from "./AuthContext";
import { useSupabaseSync } from "@/hooks/useSupabaseSync";
import { useFinanceState } from "@/hooks/finance/useFinanceState";
import { useTransactionsOperations } from "@/hooks/finance/useTransactionsOperations";
import { useCategoriesOperations } from "@/hooks/finance/useCategoriesOperations";
import { useAccountsOperations } from "@/hooks/finance/useAccountsOperations";
import { useCostCentersOperations } from "@/hooks/finance/useCostCentersOperations";
import { usePaymentMethodsOperations } from "@/hooks/finance/usePaymentMethodsOperations";
import { useSuppliersOperations } from "@/hooks/finance/useSuppliersOperations";
import { useSupabaseRealtimeSync } from "@/hooks/finance/useSupabaseRealtimeSync";
import { useDataAssignments } from "@/hooks/useDataAssignments";

export type TransactionType = "receita" | "despesa";

// Nível 1 - Categorias sintéticas principais (ex: RECEITA/FATURAMENTO)
export interface ChartOfAccountsLevel1 {
  id: string;
  code: string;
  name: string;
  color?: string;
}

// Nível 2 - Subcategorias sintéticas (ex: Receita de Vendas)
export interface ChartOfAccountsLevel2 {
  id: string;
  level1Id: string;
  code: string;
  name: string;
  type: TransactionType;
  color?: string;
}

// Nível 3 - Contas analíticas (onde os lançamentos são feitos)
export interface Category {
  id: string;
  name: string;
  color?: string;
  hidden?: boolean;
  type?: TransactionType;
  dreCategory?: string;
  level2Id?: string;  // Vinculo com nível 2
  code?: string;      // Código ex: "1.1.1"
}

export interface Account {
  id: string;
  name: string;
  color?: string;
  initialBalance?: number;
  agencia?: string;
  contaCorrente?: string;
}

export interface CostCenter {
  id: string;
  name: string;
}

export interface PaymentMethod {
  id: string;
  name: string;
}

export interface Supplier {
  id: string;
  name: string;
  cnpj?: string;
  anexo?: string;
}

export interface Transaction {
  id: string;
  description: string;
  value: number;
  type: TransactionType;
  categoryId: string;
  accountId: string;
  issueDate?: string;
  dueDate?: string;
  paymentDate?: string;
  settled?: boolean;
  parcelMode?: "total" | "parcela";
  parcels?: number | null;
  parcelIndex?: number | null;
  costCenterId?: string;
  paymentMethodId?: string;
  supplierId?: string;
  issueDateMode?: "fixed" | "followDue";
  kind?: "transferencia" | "normal";
  transferGroupId?: string | null;
  saleId?: string | null;
  purchaseId?: string | null;
  reconciled?: boolean;
  reconciledAt?: string | null;
  ofxFitid?: string | null;
  ofxImportId?: string | null;
}

interface FinanceContextValue {
  referenceDate: Date;
  setReferenceDate: (d: Date) => void;
  categories: Category[];
  accounts: Account[];
  transactions: Transaction[];
  costCenters: CostCenter[];
  paymentMethods: PaymentMethod[];
  suppliers: Supplier[];
  addTransaction: (t: Omit<Transaction, "id">) => Promise<void>;
  updateTransaction: (id: string, patch: Partial<Transaction>) => Promise<void>;
  removeTransaction: (id: string) => Promise<void>;
  toggleTransactionSettled: (id: string) => Promise<void>;
  bulkUpdateTransactions: (ids: string[], updates: Partial<Transaction>) => Promise<void>;
  bulkAddTransactions: (items: Array<Omit<Transaction, "id">>, onProgress?: (d: number, t: number) => void) => Promise<{ inserted: number; failed: number; errors: string[] }>;
  bulkRemoveTransactions: (ids: string[], onProgress?: (d: number, t: number) => void) => Promise<{ deleted: number; failed: number; errors: string[] }>;
  addCategory: (
    name: string,
    color?: string,
    type?: TransactionType,
    dreCategory?: string,
    hierarchy?: { level2Id?: string; code?: string }
  ) => Promise<string | null>;
  updateCategory: (id: string, name?: string, color?: string, type?: TransactionType, dreCategory?: string) => Promise<void>;
  
  addAccount: (name: string, color?: string, initialBalance?: number, extra?: { agencia?: string; contaCorrente?: string }) => Promise<string | null>;
  updateAccount: (id: string, name?: string, color?: string, initialBalance?: number, extra?: { agencia?: string; contaCorrente?: string }) => Promise<void>;
  removeCategory: (id: string) => Promise<boolean>;
  removeAccount: (id: string) => Promise<void>;
  addCostCenter: (name: string) => Promise<string | null>;
  updateCostCenter: (id: string, name: string) => Promise<void>;
  removeCostCenter: (id: string) => Promise<void>;
  addPaymentMethod: (name: string) => Promise<string | null>;
  updatePaymentMethod: (id: string, name: string) => Promise<void>;
  removePaymentMethod: (id: string) => Promise<void>;
  addSupplier: (name: string, extra?: { cnpj?: string; anexo?: string }) => Promise<string | null>;
  updateSupplier: (id: string, name: string, extra?: { cnpj?: string; anexo?: string }) => Promise<void>;
  removeSupplier: (id: string) => Promise<void>;
  syncing: boolean;
  realtimeConnected: boolean;
  lastSyncTime: Date | null;
  forceRefresh: (opts?: { force?: boolean }) => Promise<void>;
}

const FinanceContext = FinanceContextObject as React.Context<FinanceContextValue | null>;

export function FinanceProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const supabaseSync = useSupabaseSync();
  
  // Data assignments for member filtering
  const { filterByAssignments, loading: assignmentsLoading } = useDataAssignments();
  
  // State management
  const { state, setState, referenceDate, setReferenceDate } = useFinanceState(user?.id);
  
  // Realtime sync with data assignments filtering
  const { syncing, realtimeConnected, lastSyncTime, syncFromSupabase, forceRefresh } = 
    useSupabaseRealtimeSync(user, setState, supabaseSync, filterByAssignments);
  
  // Track if initial assignments have loaded and re-sync when they do
  const hasInitialSyncRef = useRef(false);
  const prevAssignmentsLoadingRef = useRef(true);
  
  useEffect(() => {
    // When assignments finish loading for the first time, re-sync to apply filters
    if (prevAssignmentsLoadingRef.current && !assignmentsLoading && user && hasInitialSyncRef.current) {
      // Assignments just finished loading, trigger a re-sync
      syncFromSupabase();
    }
    prevAssignmentsLoadingRef.current = assignmentsLoading;
    
    if (!assignmentsLoading && user) {
      hasInitialSyncRef.current = true;
    }
  }, [assignmentsLoading, user, syncFromSupabase]);
  
  // Operations hooks
  const transactionsOps = useTransactionsOperations(state, setState, user, supabaseSync.createTransaction, supabaseSync.updateTransaction, supabaseSync.deleteTransaction, syncFromSupabase, supabaseSync.bulkCreateTransactions, supabaseSync.bulkDeleteTransactions);
  const categoriesOps = useCategoriesOperations(state, setState, user, supabaseSync.createChartOfAccount, supabaseSync.updateChartOfAccount, supabaseSync.deleteChartOfAccount);
  const accountsOps = useAccountsOperations(state, setState, user, supabaseSync.createAccount, supabaseSync.updateAccount, supabaseSync.deleteAccount);
  const costCentersOps = useCostCentersOperations(state, setState, user, supabaseSync.createCostCenter, supabaseSync.updateCostCenter, supabaseSync.deleteCostCenter);
  const paymentMethodsOps = usePaymentMethodsOperations(state, setState, user, supabaseSync.createPaymentMethod, supabaseSync.updatePaymentMethod, supabaseSync.deletePaymentMethod);
  const suppliersOps = useSuppliersOperations(state, setState, user, supabaseSync.createSupplier, supabaseSync.updateSupplier, supabaseSync.deleteSupplier);

  const value = useMemo<FinanceContextValue>(
    () => ({
      referenceDate,
      setReferenceDate,
      ...state,
      ...transactionsOps,
      ...categoriesOps,
      ...accountsOps,
      ...costCentersOps,
      ...paymentMethodsOps,
      ...suppliersOps,
      syncing,
      realtimeConnected,
      lastSyncTime,
      forceRefresh,
    }),
    [
      referenceDate,
      setReferenceDate,
      state,
      transactionsOps,
      categoriesOps,
      accountsOps,
      costCentersOps,
      paymentMethodsOps,
      suppliersOps,
      syncing,
      realtimeConnected,
      lastSyncTime,
      forceRefresh,
    ]
  );

  return <FinanceContext.Provider value={value}>{children}</FinanceContext.Provider>;
}

export const useFinance = () => {
  const ctx = useContext(FinanceContext);
  if (!ctx) {
    console.error("[useFinance] Context not found. This usually means FinanceProvider is not in the component tree.");
    throw new Error("useFinance must be used within FinanceProvider");
  }
  return ctx;
};

export const isSameMonthISO = (iso: string, d: Date) => {
  return isSameMonth(parseISO(iso), d);
};
